import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import NavItem from '../common/NavItem'
import SessionCard from '../common/SessionCard'
import MiddlewareStatusModal from '../common/MiddlewareStatusModal'
import api from '../../utils/api'
import logo from '../../assets/logo.png'

// ── Status config ──────────────────────────────────────────────────────────────
const STATUS_CFG = {
    healthy:      { label: 'Online',       dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
    unhealthy:    { label: 'Offline',      dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400'   },
    connected:    { label: 'Online',       dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
    disconnected: { label: 'Offline',      dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400'   },
    checking:     { label: 'Verificando…', dot: 'bg-yellow-400', text: 'text-yellow-600 dark:text-yellow-400' },
    success:      { label: 'Online',       dot: 'bg-green-500',  text: 'text-green-600 dark:text-green-400' },
    warning:      { label: 'Atenção',      dot: 'bg-yellow-500', text: 'text-yellow-600 dark:text-yellow-400' },
    error:        { label: 'Offline',      dot: 'bg-red-500',    text: 'text-red-600 dark:text-red-400'   },
}

const StatusDot = ({ status }) => {
    const cfg = STATUS_CFG[status] || STATUS_CFG.checking
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${cfg.dot}`} />
            {cfg.label}
        </span>
    )
}

const SidebarServiceStatus = () => {
    const [apiStatus,         setApiStatus]         = useState('checking')
    const [dbStatus,          setDbStatus]          = useState('checking')
    const [middlewareStatus,  setMiddlewareStatus]  = useState('checking')
    const [middlewareDetails, setMiddlewareDetails] = useState(null)
    const [middlewareOpen,    setMiddlewareOpen]    = useState(false)
    const [middlewareRefreshing, setMiddlewareRefreshing] = useState(false)
    const [loading,           setLoading]           = useState(false)

    const check = useCallback(async () => {
        setLoading(true)
        setApiStatus('checking')
        setDbStatus('checking')
        setMiddlewareStatus('checking')
        try {
            await api.get('/health', { silentLoading: true })
            setApiStatus('healthy')
        } catch {
            setApiStatus('unhealthy')
        }
        try {
            const res = await api.get('/setup/status', { silentLoading: true })
            setDbStatus(res.data.database_connected ? 'connected' : 'disconnected')
            setMiddlewareStatus(res.data.middleware?.status || 'error')
            setMiddlewareDetails(res.data.middleware || null)
        } catch {
            setDbStatus('disconnected')
            setMiddlewareStatus('error')
            setMiddlewareDetails(null)
        }
        setLoading(false)
    }, [])

    const refreshMiddleware = useCallback(async () => {
        setMiddlewareRefreshing(true)
        try {
            const res = await api.get('/setup/status', { silentLoading: true })
            setMiddlewareStatus(res.data.middleware?.status || 'error')
            setMiddlewareDetails(res.data.middleware || null)
        } catch {
            setMiddlewareStatus('error')
            setMiddlewareDetails(null)
        }
        setMiddlewareRefreshing(false)
    }, [])

    useEffect(() => { check() }, [check])

    const SkeletonRow = () => (
        <div className="flex items-center gap-2 animate-pulse">
            <span className="w-3 h-3 rounded bg-gray-200 dark:bg-gray-700 shrink-0" />
            <span className="w-10 h-2.5 rounded bg-gray-200 dark:bg-gray-700" />
            <span className="w-16 h-2.5 rounded bg-gray-200 dark:bg-gray-700" />
        </div>
    )

    return (
        <div>
            <div className="h-px bg-gray-200 dark:bg-gray-700 mb-3" />
            <div className="flex items-center justify-between">
                <div className="space-y-2">
                    {loading ? (
                        <>
                            <SkeletonRow />
                            <SkeletonRow />
                            <SkeletonRow />
                        </>
                    ) : (
                        <div className="grid grid-cols-[auto_auto_auto] items-center justify-items-start gap-x-2 gap-y-2">
                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>cloud</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">API</span>
                            <StatusDot status={apiStatus} />

                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>storage</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Banco</span>
                            <StatusDot status={dbStatus} />

                            <span className="material-icons text-gray-400 dark:text-gray-500" style={{ fontSize: 13 }}>hub</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">Middleware</span>
                            <span className="flex items-center gap-1.5">
                                <StatusDot status={middlewareStatus} />
                                <button
                                    type="button"
                                    onClick={() => setMiddlewareOpen(true)}
                                    title="Ver detalhes do middleware"
                                    className="shrink-0 inline-flex items-center justify-center w-4 h-4 rounded text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
                                >
                                    <span className="material-icons leading-none" style={{ fontSize: 13 }}>north_east</span>
                                </button>
                            </span>
                        </div>
                    )}
                </div>
                <button
                    onClick={check}
                    disabled={loading}
                    title="Atualizar status"
                    className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition disabled:opacity-40"
                >
                    <span className={`material-icons ${loading ? 'animate-spin' : ''}`} style={{ fontSize: 15 }}>refresh</span>
                </button>
            </div>
            <MiddlewareStatusModal
                isOpen={middlewareOpen}
                onClose={() => setMiddlewareOpen(false)}
                status={middlewareDetails?.status || middlewareStatus}
                services={middlewareDetails?.services || []}
                error={middlewareDetails?.error}
                onRefresh={refreshMiddleware}
                refreshing={middlewareRefreshing}
            />
        </div>
    )
}

// ── Compact avatar for collapsed footer ─────────────────────────────────────────
const CollapsedFooter = ({ userEmail, onLogout }) => (
    <div className="flex flex-col items-center gap-2">
        <div
            className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-900/25 flex items-center justify-center"
            title={userEmail || 'Usuário'}
        >
            <span className="material-icons text-blue-600 dark:text-blue-400" style={{ fontSize: '17px' }}>account_circle</span>
        </div>
        <button
            onClick={onLogout}
            title="Sair"
            className="w-9 h-9 flex items-center justify-center rounded-lg text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition"
        >
            <span className="material-icons" style={{ fontSize: '17px' }}>logout</span>
        </button>
    </div>
)

// ── Sidebar ────────────────────────────────────────────────────────────────────
const Sidebar = ({ currentPage, onPageChange, userEmail, onRenew, isRenewing, onLogout, collapsed, onToggleCollapsed }) => {
    const { token, isAdmin, userType } = useAuth();

    if (!token) return null;

    return (
        <div className="relative h-full flex flex-col">
            {/* Collapse toggle */}
            <button
                onClick={onToggleCollapsed}
                title={collapsed ? 'Expandir menu' : 'Recolher menu'}
                className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition z-50"
            >
                <span className="material-icons" style={{ fontSize: '18px' }}>menu</span>
            </button>

            {/* Header */}
            <div className={`border-b border-gray-200 dark:border-gray-700 transition-all duration-300 ${collapsed ? 'px-2 pt-11 pb-6' : 'px-4 py-6'}`}>
                <div className={`flex items-center mb-3 ${collapsed ? 'justify-center' : 'gap-3'}`}>
                    <div className="logo-spin-border inline-flex items-center justify-center shrink-0" style={{}}>
                        <img
                            src={logo}
                            alt="SF"
                            className={`object-contain rounded-full transition-all duration-300 ${collapsed ? 'w-9 h-9' : 'w-14 h-14'}`}
                        />
                    </div>
                    {!collapsed && (
                        <div>
                            <h1 className="text-md" style={{ fontFamily: 'Blare, sans-serif', color: 'inherit' }}>
                                <span className="text-gray-900 dark:text-white">Solid</span><span style={{ color: '#25C6A4' }}>.</span><span className="text-gray-900 dark:text-white">Flow</span>
                            </h1>
                            <p className="text-xs text-gray-600 dark:text-gray-400" style={{ fontFamily: "'Stolzl', sans-serif" }}>Hub de Integrações</p>
                        </div>
                    )}
                </div>
                {!collapsed && <SidebarServiceStatus />}
            </div>

            {/* Navigation */}
            <nav className={`flex-1 overflow-y-auto overflow-x-hidden space-y-1 transition-all duration-300 ${collapsed ? 'px-2 py-5' : 'px-3 py-5'}`}>
                {!collapsed && (
                    <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                        Menu
                    </p>
                )}
                {isAdmin && (
                    <NavItem
                        icon={<span className="material-icons" style={{ fontSize: '18px' }}>admin_panel_settings</span>}
                        label="Usuários"
                        subtitle="Administradores"
                        active={currentPage === 'admin-users'}
                        onClick={() => onPageChange('admin-users')}
                        collapsed={collapsed}
                    />
                )}
                <NavItem
                    icon={<span className="material-icons" style={{ fontSize: '18px' }}>domain</span>}
                    label="Empresas"
                    subtitle="Clientes cadastrados"
                    active={currentPage === 'companies'}
                    onClick={() => onPageChange('companies')}
                    collapsed={collapsed}
                />
                <NavItem
                    icon={<span className="material-icons" style={{ fontSize: '18px' }}>bolt</span>}
                    label="Integrações"
                    subtitle="Conectores e eventos"
                    active={currentPage === 'integrations'}
                    onClick={() => onPageChange('integrations')}
                    collapsed={collapsed}
                />
                {isAdmin && (
                    <NavItem
                        icon={<span className="material-icons" style={{ fontSize: '18px' }}>build</span>}
                        label="Manutenção"
                        subtitle="Banco e SQL"
                        active={currentPage === 'sql'}
                        onClick={() => onPageChange('sql')}
                        collapsed={collapsed}
                    />
                )}
                <NavItem
                    icon={<span className="material-icons" style={{ fontSize: '18px' }}>settings</span>}
                    label="Cadastros"
                    subtitle="Parâmetros do sistema"
                    active={currentPage === 'settings'}
                    onClick={() => onPageChange('settings')}
                    collapsed={collapsed}
                />
            </nav>

            {/* Session Card at bottom */}
            <div className={`border-t border-gray-200 dark:border-gray-700 transition-all duration-300 ${collapsed ? 'p-2' : 'p-4'}`}>
                {collapsed ? (
                    <CollapsedFooter userEmail={userEmail} onLogout={onLogout} />
                ) : (
                    <>
                        <p className="px-0.5 mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                            Sessão
                        </p>
                        <SessionCard token={token} userEmail={userEmail} userType={userType} onLogout={onLogout} onRenew={onRenew} isRenewing={isRenewing} />
                    </>
                )}
            </div>
        </div>
    );
};

export default Sidebar
